import React, { useMemo } from 'react';
import { arrowMarkerId, gradientFadeId } from './glyphs';
import { TimelineLayoutResult } from './layout';
import { DayScale } from './scale';
import { ETItem, ETTrigger } from './types';

// The single overlay for bundle connectors, trigger arrows and
// variable-start fading trails. Suppressed entirely while a drag is in
// progress (the caller passes `suppressed`) so ~150 lines of overlay don't
// recompute on every drag tick.
export const Connectors = React.memo(
  ({
    items,
    layout,
    scale,
    triggers,
    suppressed
  }: {
    items: ETItem[];
    layout: TimelineLayoutResult;
    scale: DayScale;
    triggers: ETTrigger[];
    suppressed?: boolean;
  }) => {
    const itemById = useMemo(
      () => new Map(items.map((it) => [it.id, it])),
      [items]
    );

    const bundleLines = useMemo(() => {
      if (suppressed) return [];
      return layout.bundleSegments
        .map((seg) => {
          const parent = itemById.get(seg.bundleParentId);
          const child = itemById.get(seg.childId);
          const parentPos = layout.items[seg.bundleParentId];
          const childPos = layout.items[seg.childId];
          if (!parent || !child || !parentPos || !childPos) return null;
          const x = scale.xOfDayCenter(parent.startDay);
          return {
            key: `${seg.bundleParentId}-${seg.childId}`,
            x,
            y1: parentPos.y + 11,
            y2: childPos.y - 11,
            color: parent.color
          };
        })
        .filter((l): l is NonNullable<typeof l> => !!l);
    }, [suppressed, layout.bundleSegments, layout.items, itemById, scale]);

    const triggerArrows = useMemo(() => {
      if (suppressed) return [];
      return triggers
        .map((t) => {
          const from = itemById.get(t.fromId);
          const to = itemById.get(t.toId);
          const fromPos = layout.items[t.fromId];
          const toPos = layout.items[t.toId];
          if (!from || !to || !fromPos || !toPos || fromPos.hidden || toPos.hidden) {
            return null;
          }
          const x1 = scale.xOfDayCenter(from.endDay);
          const y1 = fromPos.y;
          const x2raw = scale.xOfDayCenter(to.startDay);
          const y2raw = toPos.y;
          const dx = x2raw - x1;
          const dy = y2raw - y1;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;
          // shorten by 13px so the arrowhead stops at the target glyph's edge
          const x2 = x2raw - (dx / dist) * 13;
          const y2 = y2raw - (dy / dist) * 13;
          return { key: `${t.fromId}-${t.toId}`, x1, y1, x2, y2, color: from.color };
        })
        .filter((l): l is NonNullable<typeof l> => !!l);
    }, [suppressed, triggers, layout.items, itemById, scale]);

    const variableStartTrails = useMemo(() => {
      if (suppressed) return [];
      return items
        .map((it) => {
          if (!it.variableStart) return null;
          const pos = layout.items[it.id];
          if (!pos || pos.hidden) return null;
          const x = scale.xOfDayCenter(it.startDay);
          return { key: `${it.id}-vsfade`, x: x + 11, y: pos.y, color: it.color };
        })
        .filter((t): t is NonNullable<typeof t> => !!t);
    }, [suppressed, items, layout.items, scale]);

    if (suppressed) return null;

    return (
      <>
        {bundleLines.map((l) => (
          <line key={l.key} x1={l.x} y1={l.y1} x2={l.x} y2={l.y2} stroke={l.color} strokeWidth={2} />
        ))}
        {triggerArrows.map((l) => (
          <line
            key={l.key}
            x1={l.x1}
            y1={l.y1}
            x2={l.x2}
            y2={l.y2}
            stroke={l.color}
            strokeWidth={2}
            strokeDasharray="5,4"
            markerEnd={`url(#${arrowMarkerId(l.color)})`}
          />
        ))}
        {variableStartTrails.map((t) => (
          <line
            key={t.key}
            x1={t.x}
            y1={t.y}
            x2={t.x + 44}
            y2={t.y}
            stroke={`url(#${gradientFadeId(t.color)})`}
            strokeWidth={2}
            strokeDasharray="3 3"
            strokeLinecap="round"
          />
        ))}
      </>
    );
  }
);
